import { Component, OnInit, ChangeDetectionStrategy, Input, Output, EventEmitter, OnDestroy, ChangeDetectorRef, inject } from '@angular/core';
import { Subject, switchMap, take, takeUntil, tap } from 'rxjs';
import {
  DrawingToolConfigModel, DrawingToolEvent, DrawingToolModel, DrawingType, ExtTransformEnableToolArguments, ExtTransformToolConfigModel,
  ExtTransformToolModel, MapService,
  MapStyleModel,
  SelectToolConfigModel,
  SelectToolModel, ToolManagerModel, ToolTypeEnum,
} from '@tailormap-viewer/map';
import { DrawingFeatureTypeEnum } from '../../../../map';
import { BaseComponentTypeEnum, FeatureModel } from '@tailormap-viewer/api';
import { ApplicationStyleService } from '../../../../services';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { TooltipDirective } from '@tailormap-viewer/shared';

@Component({
    selector: 'tm-map-drawing-buttons',
    templateUrl: './map-drawing-buttons.component.html',
    styleUrls: ['./map-drawing-buttons.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        MatButton,
        TooltipDirective,
        MatIcon,
    ],
})
export class MapDrawingButtonsComponent implements OnInit, OnDestroy {
  private mapService = inject(MapService);
  private cdr = inject(ChangeDetectorRef);


  private static getDefaultStyle = (): Partial<MapStyleModel> => ({
    pointType: 'circle',
    strokeColor: ApplicationStyleService.getPrimaryColor(),
    pointFillColor: 'transparent',
    pointStrokeColor: ApplicationStyleService.getPrimaryColor(),
  });

  @Input()
  public drawingLayerId = '';

  private _selectedFeature: FeatureModel | null = null;
  public isSelectedFeaturePointGeometry = false;

  @Input()
  public set selectedFeature(value: FeatureModel | null) {
    this._selectedFeature = value;
    this.isSelectedFeaturePointGeometry = value?.geometry?.trim().startsWith('POINT') ?? false;
    this.enableModifyTool();
  }
  public get selectedFeature(): FeatureModel | null {
    return this._selectedFeature;
  }

  @Input()
  public selectionStyle: Partial<MapStyleModel> | ((feature: FeatureModel) => MapStyleModel) | undefined = undefined;

  @Input()
  public drawSingleShape = false;

  @Input()
  public allowRemoveSelectedFeature = false;

  @Input()
  public allowModify = false;

  @Input()
  public showSelectButton = true;

  @Input()
  public allowedShapes: DrawingFeatureTypeEnum[] | undefined = undefined;

  @Output()
  public drawingAdded: EventEmitter<DrawingToolEvent> = new EventEmitter<DrawingToolEvent>();

  @Output()
  public featureGeometryModified: EventEmitter<string> = new EventEmitter<string>();

  @Output()
  public featureRemoved: EventEmitter<FeatureModel> = new EventEmitter<FeatureModel>();

  @Output()
  public featureSelected: EventEmitter<FeatureModel | null> = new EventEmitter<FeatureModel | null>();

  @Output()
  public activeToolChanged: EventEmitter<DrawingFeatureTypeEnum | null> = new EventEmitter<DrawingFeatureTypeEnum | null>();

  public drawingTypes = DrawingFeatureTypeEnum;

  private destroyed = new Subject();
  private tool: DrawingToolModel | null = null;
  public activeTool: DrawingFeatureTypeEnum | null = null;
  private selectTool: SelectToolModel | null = null;
  private extTransformTool: ExtTransformToolModel | null = null;

  private withToolManager(
    callback: (manager: ToolManagerModel) => void,
  ) {
    this.mapService.getToolManager$().pipe(take(1)).subscribe(manager => {
      callback(manager);
    });
  }

  public ngOnInit(): void {
    this.mapService.createTool$<DrawingToolModel, DrawingToolConfigModel>({
      type: ToolTypeEnum.Draw,
      style: MapDrawingButtonsComponent.getDefaultStyle(),
      owner: BaseComponentTypeEnum.FILTER,
      disableFreeHandDrawing: true,
    })
      .pipe(
        takeUntil(this.destroyed),
        tap(({ tool }) => this.tool = tool),
        switchMap(({ tool }) => tool.drawing$),
      )
      .subscribe(drawEvent => {
        if (drawEvent && drawEvent.type === 'start') {
          this.extTransformTool?.disableTranslate();
        }
        if (drawEvent && drawEvent.type === 'end' && this.activeTool) {
          this.extTransformTool?.enableTranslate();
          this.drawingAdded.emit(drawEvent);
          if (this.drawSingleShape) {
            const activeTool = this.activeTool;
            setTimeout(() => {
              this.toggleTool(this.drawingFeatureTypeToDrawingType(activeTool), activeTool);
            }, 100);
          }
        }
      });

    this.mapService.createTool$<SelectToolModel, SelectToolConfigModel>({
      type: ToolTypeEnum.Select,
      layers: [this.drawingLayerId],
      style: this.selectionStyle || MapDrawingButtonsComponent.getDefaultStyle(),
      owner: BaseComponentTypeEnum.FILTER,
    })
      .pipe(
        takeUntil(this.destroyed),
        tap(({ tool, manager }) => {
          this.selectTool = tool;
          manager.enableTool(tool.id, true);
        }),
        switchMap(({ tool }) => tool.selectedFeatures$),
      )
      .subscribe(selectedFeatures => {
        const selectedFeature = selectedFeatures && selectedFeatures.length > 0 && selectedFeatures[0] ? selectedFeatures[0] : null;
        this.cdr.detectChanges();
        this.featureSelected.emit(selectedFeature);
      });

    const style: MapStyleModel = {
      styleKey: 'edit-geometry-style',
      zIndex: 100,
      pointType: 'circle',
      pointStrokeColor: ApplicationStyleService.getPrimaryColor(),
      pointFillColor: ApplicationStyleService.getPrimaryColor(),
      pointStrokeWidth: 2,
      pointSize: 5,
      strokeColor: ApplicationStyleService.getPrimaryColor(),
      strokeType: 'dash',
      strokeWidth: 2,
      fillColor: ApplicationStyleService.getPrimaryColor(), // Must specify color otherwise no hand cursor
      fillOpacity: 0,
    };

    if (this.allowModify) {
      this.mapService.createTool$<ExtTransformToolModel, ExtTransformToolConfigModel>({
        type: ToolTypeEnum.ExtTransform,
        style,
        owner: BaseComponentTypeEnum.FILTER,
      }).pipe(
        takeUntil(this.destroyed),
        tap(({ tool }) => {
          this.extTransformTool = tool;
        }),
        switchMap(({ tool }) => tool.featureModified$),
      ).subscribe(modifiedGeometry => {
        this.featureGeometryModified.emit(modifiedGeometry);
      });
    }
  }

  public ngOnDestroy() {
    this.destroyed.next(null);
    this.destroyed.complete();
  }

  public draw(type: DrawingFeatureTypeEnum) {
    this.toggleTool(this.drawingFeatureTypeToDrawingType(type), type);
  }

  public isAllowedShape(type: DrawingFeatureTypeEnum) {
    return !this.allowedShapes || this.allowedShapes.includes(type);
  }

  private drawingFeatureTypeToDrawingType(type: DrawingFeatureTypeEnum): DrawingType {
    const dict: { [key in DrawingFeatureTypeEnum]: DrawingType } = {
      [DrawingFeatureTypeEnum.POINT]: 'point',
      [DrawingFeatureTypeEnum.LABEL]: 'point',
      [DrawingFeatureTypeEnum.LINE]: 'line',
      [DrawingFeatureTypeEnum.POLYGON]: 'area',
      [DrawingFeatureTypeEnum.CIRCLE]: 'circle',
      [DrawingFeatureTypeEnum.CIRCLE_SPECIFIED_RADIUS]: 'point',
      [DrawingFeatureTypeEnum.SQUARE]: 'square',
      [DrawingFeatureTypeEnum.SQUARE_SPECIFIED_LENGTH]: 'point',
      [DrawingFeatureTypeEnum.RECTANGLE]: 'rectangle',
      [DrawingFeatureTypeEnum.RECTANGLE_SPECIFIED_SIZE]: 'point',
      [DrawingFeatureTypeEnum.ELLIPSE]: 'ellipse',
      [DrawingFeatureTypeEnum.STAR]: 'star',
      [DrawingFeatureTypeEnum.IMAGE]: 'point',
    };
    return dict[type];
  }

  private toggleTool(type: DrawingType, drawingFeatureType: DrawingFeatureTypeEnum) {
    this.withToolManager(manager => {
      if (!this.tool || !this.selectTool) {
        return;
      }
      if (this.activeTool === drawingFeatureType) {
        this.disableDrawing();
        this.enableModifyTool();
      } else {
        // Enable drawing
        this.activeTool = drawingFeatureType;
        manager.enableTool(this.tool.id, true, { type });
        manager.disableTool(this.selectTool.id, true);
        if (this.extTransformTool) {
          manager.disableTool(this.extTransformTool.id, true);
        }
        this.featureSelected.emit(null);
        this.activeToolChanged.emit(this.activeTool);
      }
      this.cdr.detectChanges();
    });
  }

  private disableDrawing(disableOtherTools = true) {
    if (this.activeTool === null) {
      return;
    }
    this.withToolManager(manager => {
      if (!this.tool || !this.selectTool) {
        return;
      }
      this.activeTool = null;
      manager.disableTool(this.tool.id, true);
      manager.enableTool(this.selectTool.id, disableOtherTools);
      this.activeToolChanged.emit(this.activeTool);
      this.cdr.detectChanges();
    });
  }

  public removeSelectedFeature() {
    if (!this._selectedFeature) {
      return;
    }
    this.featureRemoved.emit(this._selectedFeature);
  }

  private enableModifyTool() {
    if (!this.allowModify) {
      return;
    }
    this.withToolManager(manager => {
      if (this._selectedFeature) {
        const enableArgs: ExtTransformEnableToolArguments = {
          feature: this._selectedFeature,
          style: this.selectionStyle,
        };
        manager.enableTool(this.extTransformTool?.id || '', false, enableArgs, true);
      } else {
        manager.disableTool(this.extTransformTool?.id || '', true);
      }
    });
  }

  public enableSelectAndModify() {
    this.disableDrawing(false);
    this.enableModifyTool();
  }

}
